/**
 * Public Config — runtime configuration fetched from the API server.
 *
 * Source of truth: GET /graveforge/public-config on the API server.
 * Backed by the forge_platform_settings DB table (is_public=true rows).
 *
 * Call `loadPublicConfig()` once at app boot before rendering. After that,
 * `getConfig(key, defaultValue)` returns values synchronously from cache.
 *
 * Falls back to VITE_* env vars if the fetch fails (for dev/offline safety).
 */

const SERVER_URL = import.meta.env.VITE_SERVER_URL || 'http://localhost:3002';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min stale-while-revalidate

let _cache = null;
let _cacheLoadedAt = 0;
let _loadPromise = null;

const ENV_FALLBACKS = {
  'app.name': () => import.meta.env.VITE_APP_NAME || 'GraveForge',
  'app.preview_limit': () => Number(import.meta.env.VITE_PREVIEW_LIMIT) || 100,
  'app.enable_watermark': () => import.meta.env.VITE_ENABLE_WATERMARK === 'true',
  'app.watermark_text': () => import.meta.env.VITE_WATERMARK_TEXT || 'GraveForge Preview',
  'pricing.per_nft_usd': () => Number(import.meta.env.VITE_PRICE_PER_NFT_USD) || 0.20,
  'pricing.min_order_usd': () => Number(import.meta.env.VITE_MIN_ORDER_USD) || 1.00,
  'pricing.max_order_usd': () => Number(import.meta.env.VITE_MAX_ORDER_USD) || 10000.00,
  'pricing.server_generation_multiplier': () => Number(import.meta.env.VITE_SERVER_GENERATION_PRICE_MULTIPLIER) || 1.33,
  'pricing.animated_cost': () => Number(import.meta.env.VITE_ANIMATED_PRICE_COST) || 2.00,
  'pricing.animated_multiplier': () => Number(import.meta.env.VITE_ANIMATED_PRICE_MULTIPLIER) || 2.0,
  'stripe.processing_fee_pct': () => Number(import.meta.env.VITE_STRIPE_PROCESSING_FEE) || 3.0,
  'stripe.tax_rate_pct': () => Number(import.meta.env.VITE_STRIPE_TAX_RATE) || 6.0,
  'features.server_generation': () => import.meta.env.VITE_ENABLE_SERVER_GENERATION === 'true',
  'features.curated': () => import.meta.env.VITE_CURATED_ENABLED !== 'false',
  'features.custom_token': () => import.meta.env.VITE_ENABLE_CUSTOM_TOKEN === 'true',
  'features.web_workers': () => import.meta.env.VITE_ENABLE_WEB_WORKERS === 'true',
  'custom_token.mint': () => import.meta.env.VITE_CUSTOM_TOKEN_MINT || '',
  'custom_token.name': () => import.meta.env.VITE_CUSTOM_TOKEN_NAME || '',
  'custom_token.symbol': () => import.meta.env.VITE_CUSTOM_TOKEN_SYMBOL || '',
  'custom_token.decimals': () => Number(import.meta.env.VITE_CUSTOM_TOKEN_DECIMALS) || 9,
  'custom_token.transfer_fee_pct': () => Number(import.meta.env.VITE_CUSTOM_TOKEN_TRANSFER_FEE) || 0,
  'custom_token.price_usd': () => Number(import.meta.env.VITE_CUSTOM_TOKEN_PRICE_USD) || 0,
  'custom_token.discount_percent': () => Number(import.meta.env.VITE_CUSTOM_TOKEN_DISCOUNT_PERCENT) || 0,
  'curated.multiplier': () => Number(import.meta.env.VITE_CURATED_MULTIPLIER) || 1.5,
  'curated.overgeneration': () => Number(import.meta.env.VITE_CURATED_OVERGENERATION) || 2.0,
  'curated.initial_retention_hours': () => Number(import.meta.env.VITE_CURATED_INITIAL_RETENTION) || 72,
  'curated.final_retention_hours': () => Number(import.meta.env.VITE_CURATED_FINAL_RETENTION) || 48,
  'curated.max_target': () => Number(import.meta.env.VITE_CURATED_MAX_TARGET) || 5000,
  'curated.min_target': () => Number(import.meta.env.VITE_CURATED_MIN_TARGET) || 10,
  // RPC endpoints are intentionally not exposed here. Use the server proxy
  // at `${VITE_SERVER_URL}/graveforge/solana-rpc` instead — the Helius API
  // key must stay server-side. (See server/routes/solanaRpc.js.)
  'features.download_proxy': () => import.meta.env.VITE_USE_DOWNLOAD_PROXY === 'true',
  'features.export_proxy': () => import.meta.env.VITE_USE_EXPORT_PROXY === 'true',
  'generation.max_worker_threads': () => Number(import.meta.env.VITE_MAX_WORKER_THREADS) || 8,
  'generation.batch_size': () => Number(import.meta.env.VITE_GENERATION_BATCH_SIZE) || 10,
  'generation.file_retention_hours': () => Number(import.meta.env.VITE_GENERATION_FILE_RETENTION) || 48,
  'generation.export_retention_hours': () => Number(import.meta.env.VITE_EXPORT_FILE_RETENTION) || 1,
  'system.maintenance_mode': () => false,
  'system.maintenance_message': () => "We're performing maintenance. Back shortly.",
  'system.read_only_mode': () => false,
  'banner.active': () => false,
  'banner.message': () => '',
  'banner.level': () => 'info',
  'banner.link_url': () => '',
  'quota.free_previews_per_day': () => 50,
  'quota.free_projects_per_wallet': () => 3,
  'invite_gate.enabled': () => false,
  'invite_gate.message': () => '',
  'subscriptions.enabled': () => false,
  'featured_gallery.enabled': () => true,
};

function envFallback(key, explicitDefault) {
  const fn = ENV_FALLBACKS[key];
  if (fn) return fn();
  return explicitDefault;
}

/**
 * Fetch public config from API server. Call once at app boot.
 * Subsequent calls return cached promise within TTL.
 * @returns {Promise<object>}
 */
export async function loadPublicConfig() {
  const now = Date.now();
  if (_cache && now - _cacheLoadedAt < CACHE_TTL_MS) return _cache;
  if (_loadPromise) return _loadPromise;

  _loadPromise = (async () => {
    try {
      const res = await fetch(`${SERVER_URL}/graveforge/public-config`, {
        method: 'GET',
        credentials: 'omit',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data?.success || !data.settings) throw new Error('Invalid response');
      _cache = data.settings;
      _cacheLoadedAt = now;
      return _cache;
    } catch (err) {
      console.warn('[publicConfig] fetch failed, using env fallbacks:', err.message);
      _cache = _cache || {};
      _cacheLoadedAt = now;
      return _cache;
    } finally {
      _loadPromise = null;
    }
  })();

  return _loadPromise;
}

/**
 * Get a single config value synchronously from cache. Falls back to env vars.
 * @param {string} key e.g. 'pricing.per_nft_usd'
 * @param {*} defaultValue
 */
export function getConfig(key, defaultValue = undefined) {
  if (_cache && key in _cache) return _cache[key];
  return envFallback(key, defaultValue);
}

/**
 * Get the entire cached config object (empty until loadPublicConfig resolves).
 */
export function getAllConfig() {
  return _cache || {};
}

/**
 * Force-refresh the cache (e.g. after admin pushes a settings change).
 */
export async function refreshPublicConfig() {
  _cache = null;
  _cacheLoadedAt = 0;
  return loadPublicConfig();
}
